import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AureloLogo, AureloMark } from "@/components/Logo";
import { DevelopedBy } from "@/components/CodeLabsLogo";
import { HeroSlideshow } from "@/components/HeroSlideshow";
import { AppPreviewTabs } from "@/components/AppPreview";
import { FadeImage } from "@/components/FadeImage";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Sparkles, Users, Megaphone, TrendingUp, HeartPulse, Shield,
  ArrowRight, Check, Zap, Brain, Calendar, Star, Bot,
  Twitter, Linkedin, Youtube, Facebook, Instagram, Globe,
} from "lucide-react";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1543286386-2e659306cd6c?auto=format&fit=crop&w=1920&q=80",
];
const BAND_IMG = "https://images.unsplash.com/photo-1531973576160-7125cd663d86?auto=format&fit=crop&w=1920&q=80";
const AI_IMG = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80";

const TRUST = [
  { name: "210 CodeLabs", src: "/brand/trust/tl_210codelabs.png", w: 150 },
  { name: "NOBISYS", src: "/brand/trust/tl_nobisys.png", w: 128 },
  { name: "NORVANE", src: "/brand/trust/tl_norvane.png", w: 124 },
  { name: "Manuel Alvariño", src: "/brand/trust/tl_manuel.png", w: 172 },
  { name: "ProactiveClinic", src: "/brand/trust/tl_proactive.png", w: 152 },
];

const FEATURES = [
  { icon: Users, title: "CRM", desc: "Contacts, companies, leads & a 360° customer view in one place." },
  { icon: Zap, title: "Sales Engagement", desc: "Multi-step sequences, tasks & AI-assisted outreach that convert." },
  { icon: Megaphone, title: "Marketing Automation", desc: "Campaigns, audiences, forms & attribution built in." },
  { icon: Sparkles, title: "AI Assistant", desc: "Aurelo AI copilot answers questions & takes action across the CRM." },
  { icon: TrendingUp, title: "Revenue Intelligence", desc: "Forecasting, deal health & pipeline analytics you can trust." },
  { icon: Calendar, title: "Google Workspace", desc: "Gmail, Calendar, Meet, Drive & Contacts — natively connected." },
  { icon: HeartPulse, title: "Customer Success", desc: "Health scores, onboarding & renewals to grow accounts." },
  { icon: Shield, title: "Enterprise Security", desc: "Multi-tenant isolation, RBAC, audit logs & white-label." },
];

const STATS = [
  ["2.4M+", "Pipeline managed"],
  ["58%", "Avg. win rate"],
  ["34d", "Faster sales cycle"],
  ["10k+", "Revenue teams"],
];

const PLANS = [
  { name: "Starter", price: "$49", features: ["3 users", "1,000 contacts", "Pipeline & tasks", "Email sequences"] },
  { name: "Professional", price: "$99", popular: true, features: ["10 users", "25,000 contacts", "AI Copilot", "Workflows & campaigns"] },
  { name: "Business", price: "$199", features: ["Unlimited users", "AI SDR agents", "Advanced reports", "Custom objects"] },
  { name: "Enterprise", price: "Custom", features: ["SSO & white-label", "Dedicated success", "API & webhooks", "Priority support"] },
];

const FOOTER_COLS = [
  { title: "Product", links: [
    ["Features", "#features"], ["Aurelo AI", "#ai"], ["Pricing", "#pricing"],
    ["Integrations", "#"], ["API", "#"], ["MCP", "#"], ["Product updates", "#"],
  ] },
  { title: "Discover", links: [
    ["Partner program", "#"], ["Referral program", "#"], ["Case studies", "#"],
    ["Sales pipeline course", "#"], ["What is CRM?", "#"], ["Resources", "#"], ["Sales calculator", "#"],
  ] },
  { title: "Help Center", links: [
    ["Knowledge Base", "#"], ["Academy", "#"], ["FAQs", "#"], ["Status", "#"],
  ] },
  { title: "Legal hub", links: [
    ["Sub-processors", "#"], ["Terms of Service", "/legal/terms"], ["Privacy Notice", "/legal/privacy"],
    ["Cookie Notice", "/legal/cookies"], ["Site map", "#"], ["Your Privacy Choices", "#"],
  ] },
];
const FOOTER_SOCIALS = [
  [Twitter, "Twitter / X"], [Linkedin, "LinkedIn"], [Youtube, "YouTube"],
  [Facebook, "Facebook"], [Instagram, "Instagram"],
];

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToPricing = () => {
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Scroll-aware nav */}
      <header
        data-testid="landing-nav"
        className={cn(
          "fixed top-0 inset-x-0 z-50 transition-colors duration-300",
          scrolled ? "bg-white/95 backdrop-blur-md border-b border-slate-200/70 shadow-[0_4px_20px_rgba(11,16,43,0.06)]" : "bg-transparent"
        )}
      >
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <AureloLogo size={30} light={!scrolled} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
            {[["Features", "#features"], ["AI", "#ai"], ["Pricing", "#pricing"]].map(([label, href]) => (
              <a key={href} href={href}
                className={cn("transition-colors", scrolled ? "text-slate-600 hover:text-slate-900" : "text-white/80 hover:text-white")}>
                {label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/login")}
              data-testid="landing-signin"
              className={cn("h-9 px-3.5 rounded-lg text-sm font-medium transition-colors",
                scrolled ? "text-slate-700 hover:bg-slate-100" : "text-white hover:bg-white/10")}
            >
              Sign in
            </button>
            <Button className="aurelo-gradient text-white h-9" onClick={() => navigate("/signup")} data-testid="landing-hero-primary-cta">Start Free</Button>
          </div>
        </div>
      </header>

      {/* Hero — full-bleed rotating slideshow background */}
      <section className="relative overflow-hidden bg-[#0B102B] text-white">
        <HeroSlideshow images={HERO_IMAGES} interval={6000} />
        <Reveal y={16} className="relative z-10 max-w-6xl mx-auto px-5 pt-36 pb-48 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white/90 mb-6 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-[#A855F7]" /> AI-native Revenue Operating System
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] font-extrabold tracking-tight">
            <span className="aurelo-gradient-text">Connect. Engage.</span><br />Close. Grow.
          </h1>
          <p className="mt-6 text-lg text-slate-200/90 max-w-2xl mx-auto">
            Aurelo unifies CRM, sales automation, marketing, AI intelligence, and customer success in one intelligent revenue platform.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3">
            <Button size="lg" className="aurelo-gradient text-white h-12 px-6 text-base shadow-[0_16px_40px_rgba(124,58,237,0.35)]" onClick={() => navigate("/signup")}>
              Start Free Trial <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-6 text-base bg-white/5 border-white/25 text-white hover:bg-white/10 hover:text-white" onClick={scrollToPricing} data-testid="landing-hero-secondary-cta">
              See Pricing
            </Button>
          </div>
          <p className="mt-4 text-xs text-white/50">7-day free trial · Cancel anytime</p>
        </Reveal>
      </section>

      {/* App previews — floating browser frame that overlaps the hero */}
      <div className="relative z-20 max-w-5xl mx-auto px-5 -mt-40">
        <AppPreviewTabs />
      </div>

      {/* Trusted-by logo strip */}
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-4" data-testid="trust-bar">
        <Reveal className="text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Trusted by teams building what&apos;s next</p>
        </Reveal>
        <Reveal delay={120} className="mt-8 flex flex-wrap items-center justify-center gap-x-10 md:gap-x-14 gap-y-7">
          {TRUST.map((l) => (
            <img key={l.name} src={l.src} alt={l.name} loading="lazy"
              className="w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-200"
              style={{ maxHeight: 32, maxWidth: l.w }} />
          ))}
        </Reveal>
      </section>

      {/* Stats strip */}
      <section className="max-w-6xl mx-auto px-5 pt-12 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map(([v, l], i) => (
            <Reveal key={l} delay={i * 80} className="text-center">
              <div className="text-3xl md:text-4xl font-extrabold aurelo-gradient-text tracking-tight tabular-nums">{v}</div>
              <div className="text-sm text-slate-500 mt-1">{l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-20">
        <Reveal className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight">Everything your revenue team needs</h2>
          <p className="text-slate-600 mt-2">One intelligent platform replacing your entire GTM stack.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 4) * 70}>
              <div className="group h-full rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-[0_12px_30px_rgba(11,16,43,0.1)] hover:-translate-y-0.5 transition-all">
                <div className="h-10 w-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center group-hover:aurelo-gradient transition-colors">
                  <f.icon className="h-5 w-5 text-[#7C3AED] group-hover:text-white transition-colors" />
                </div>
                <div className="mt-3 font-semibold">{f.title}</div>
                <div className="text-sm text-slate-500 mt-1">{f.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Full-bleed blended image band (no boxes) */}
      <section className="relative overflow-hidden bg-[#0B102B] text-white">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={BAND_IMG} alt="" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #0B102B 0%, rgba(11,16,43,0.92) 44%, rgba(11,16,43,0.45) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #0B102B 0%, rgba(11,16,43,0) 26%, rgba(11,16,43,0) 68%, #0B102B 100%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(70% 60% at 82% 30%, rgba(124,58,237,0.30) 0%, rgba(6,182,212,0.10) 42%, rgba(0,0,0,0) 72%)" }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 py-28">
          <Reveal className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white mb-4 backdrop-blur">
              <Star className="h-3.5 w-3.5 text-[#A855F7]" /> Built for modern revenue teams
            </div>
            <h2 className="text-3xl md:text-[40px] leading-tight font-extrabold tracking-tight">
              One workspace where sales, marketing &amp; success win together.
            </h2>
            <p className="mt-4 text-slate-300 text-lg">
              Replace a dozen disconnected tools with a single intelligent surface — every contact, deal and conversation in perfect context.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {["Unified customer timeline", "AI on every record", "Real-time revenue intelligence", "Automated follow-ups"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-slate-200"><Check className="h-4 w-4 text-[#10B981]" />{x}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-20">
        <Reveal className="text-center mb-12"><h2 className="text-3xl font-bold tracking-tight">Simple, scalable pricing</h2><p className="text-slate-600 mt-2">Start free. Grow into enterprise.</p></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 4) * 70}>
              <div className={cn("h-full rounded-2xl border bg-white p-6 relative transition-shadow", p.popular ? "border-[#7C3AED] shadow-[0_16px_40px_rgba(124,58,237,0.16)]" : "border-slate-200 hover:shadow-[0_8px_24px_rgba(11,16,43,0.08)]")}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-bold text-white aurelo-gradient rounded-full px-3 py-1">Most Popular</div>}
                <div className="font-semibold">{p.name}</div>
                <div className="mt-2"><span className="text-3xl font-extrabold">{p.price}</span>{p.price !== "Custom" && <span className="text-slate-400 text-sm">/mo</span>}</div>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">{p.features.map((f) => <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#10B981]" />{f}</li>)}</ul>
                <Button className={cn("w-full mt-6", p.popular && "aurelo-gradient text-white")} variant={p.popular ? "default" : "outline"} onClick={() => navigate("/signup")}>Get Started</Button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AI band */}
      <section id="ai" className="relative overflow-hidden bg-[#0B102B] text-white">
        <div className="absolute -top-32 right-0 h-[380px] w-[380px] rounded-full aurelo-gradient opacity-20 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-4 backdrop-blur"><Brain className="h-3.5 w-3.5" />AI that actually works with you</div>
            <h2 className="text-3xl md:text-[40px] leading-tight font-extrabold tracking-tight">Meet Aurelo AI</h2>
            <p className="text-slate-300 mt-4 text-lg">From lead scoring and account research to deal intelligence, email personalization and autonomous SDR agents — Aurelo is AI-native, not AI-added.</p>
            <ul className="mt-6 space-y-3">
              {["AI lead & deal scoring", "Autonomous AI SDR agents", "Natural-language CRM actions", "Revenue forecasting"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-slate-200"><Check className="h-4 w-4 text-[#10B981]" />{x}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={140} className="relative rounded-2xl bg-white/5 border border-white/10 p-5 backdrop-blur">
            <div className="absolute -top-8 -right-8 h-44 w-44 opacity-70 hidden lg:block pointer-events-none">
              <FadeImage src={AI_IMG} alt="" ratio="100%" fade="all" rounded="9999px" />
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold"><AureloMark size={22} light />Aurelo AI</div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="rounded-xl bg-white/10 px-3 py-2 ml-auto max-w-[80%]">Which deals are most likely to close this month?</div>
              <div className="rounded-xl bg-white text-slate-800 px-3 py-2.5 max-w-[92%]">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#7C3AED] mb-1"><Bot className="h-3.5 w-3.5" />Aurelo AI</div>
                3 deals show strong momentum: <b>ACME</b> ($185K, 72%), <b>GlobalTech</b> ($120K, 68%), <b>Vision Labs</b> ($95K, 76%). I recommend prioritizing Vision Labs first.
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <Reveal>
          <div className="rounded-3xl aurelo-gradient p-10 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(60% 100% at 50% 0%, #fff 0%, rgba(255,255,255,0) 60%)" }} />
            <h2 className="relative text-3xl md:text-4xl font-extrabold tracking-tight">Turn your CRM into a revenue engine.</h2>
            <p className="relative mt-3 text-white/90">Start with Aurelo today — your team will feel the difference on day one.</p>
            <div className="relative mt-7 flex items-center justify-center gap-3">
              <Button size="lg" className="bg-white text-[#0B102B] hover:bg-white/90 h-12 px-6" onClick={() => navigate("/signup")}>Start Free Trial</Button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B102B] text-slate-300" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-10">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-10">
            {/* Brand */}
            <div className="col-span-2">
              <AureloLogo size={28} light />
              <p className="text-sm text-slate-400 mt-4 max-w-xs leading-relaxed">
                The AI-native Revenue Operating System — CRM, sales, marketing & customer success in one intelligent platform.
              </p>
              <div className="mt-5 flex items-center gap-2">
                {FOOTER_SOCIALS.map(([Icon, label]) => (
                  <a key={label} href="#" aria-label={label} title={label}
                    className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
            {/* Link columns */}
            {FOOTER_COLS.map((col) => (
              <div key={col.title} className="col-span-1">
                <div className="text-sm font-semibold text-white mb-4">{col.title}</div>
                <ul className="space-y-2.5">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith("/") ? (
                        <Link to={href} className="text-sm text-slate-400 hover:text-white transition-colors">{label}</Link>
                      ) : (
                        <a href={href} className="text-sm text-slate-400 hover:text-white transition-colors">{label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-2">
              <span className="text-sm text-slate-400">© 2026 Aurelo. All rights reserved.</span>
              <button className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
                <Globe className="h-4 w-4" /> English (US)
              </button>
              <Link to="/legal/terms" className="text-sm text-slate-400 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/legal/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Notice</Link>
              <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Your Privacy Choices</a>
            </div>
            <DevelopedBy labelColor="#64748B" height={22} />
          </div>
        </div>
      </footer>
    </div>
  );
}
